import { useEffect, useState } from "react";
import { DownloadSimpleIcon, XIcon, ShareIcon, LightningIcon } from "@phosphor-icons/react";

const DISMISS_KEY = "chargesure_pwa_dismissed";

function isStandalone() {
  return window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
}
function isIOS() {
  return /iphone|ipad|ipod/i.test(window.navigator.userAgent) && !window.MSStream;
}

export default function PWAInstall() {
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);
  const [iosHint, setIosHint] = useState(false);

  useEffect(() => {
    if (isStandalone() || localStorage.getItem(DISMISS_KEY)) return;
    const onPrompt = (e) => { e.preventDefault(); setDeferred(e); setShow(true); };
    window.addEventListener("beforeinstallprompt", onPrompt);
    const onInstalled = () => { setShow(false); setDeferred(null); };
    window.addEventListener("appinstalled", onInstalled);
    // iOS Safari has no beforeinstallprompt — offer manual guidance
    if (isIOS() && !isStandalone()) setShow(true);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const dismiss = () => { setShow(false); setIosHint(false); localStorage.setItem(DISMISS_KEY, "1"); };

  const install = async () => {
    if (deferred) {
      deferred.prompt();
      const { outcome } = await deferred.userChoice;
      if (outcome === "accepted") setShow(false);
      setDeferred(null);
    } else if (isIOS()) {
      setIosHint(true);
    }
  };

  if (!show) return null;

  return (
    <>
      <div data-testid="pwa-install-banner" className="fixed bottom-4 left-1/2 z-[1000] w-[92%] max-w-md -translate-x-1/2 rounded-2xl border border-emerald-200 bg-white p-3 shadow-xl shadow-emerald-900/10">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-emerald-600 text-white">
            <LightningIcon weight="fill" size={22} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-display text-sm font-semibold text-slate-900">Install ChargeSure</div>
            <div className="text-xs text-slate-500">Add it to your home screen for one-tap access.</div>
          </div>
          <button data-testid="pwa-install-btn" onClick={install}
            className="inline-flex shrink-0 items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-2 text-sm font-semibold text-white transition-colors hover:bg-emerald-700">
            <DownloadSimpleIcon size={16} /> Install
          </button>
          <button data-testid="pwa-dismiss-btn" onClick={dismiss} className="shrink-0 rounded-lg p-1.5 text-slate-400 hover:bg-slate-100" title="Dismiss">
            <XIcon size={16} />
          </button>
        </div>
      </div>

      {iosHint && (
        <div className="fixed inset-0 z-[1001] flex items-end justify-center bg-black/30 p-4" onClick={() => setIosHint(false)}>
          <div className="w-full max-w-sm rounded-2xl bg-white p-5 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="font-display text-base font-semibold text-slate-900">Install on iPhone / iPad</div>
            <ol className="mt-3 space-y-2 text-sm text-slate-600">
              <li className="flex items-center gap-2">1. Tap the <ShareIcon size={16} className="inline text-sky-600" /> Share button in Safari</li>
              <li>2. Choose <span className="font-medium text-slate-800">"Add to Home Screen"</span></li>
              <li>3. Tap <span className="font-medium text-slate-800">Add</span> — done!</li>
            </ol>
            <button onClick={dismiss} className="mt-4 w-full rounded-lg bg-emerald-600 py-2 text-sm font-semibold text-white">Got it</button>
          </div>
        </div>
      )}
    </>
  );
}
